import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BookOpen, ChevronLeft, Search, Loader2, X } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function highlight(text, q) {
  if (!q || q.length < 2) return text;
  try {
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${safe})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase() ? (
        <mark key={`m-${i}-${p}`} className="bg-[color:var(--papicoo-gold)]/40 text-[color:var(--papicoo-ink)] rounded px-0.5">{p}</mark>
      ) : (
        <span key={`s-${i}-${p}`}>{p}</span>
      )
    );
  } catch {
    return text;
  }
}

export default function Coran() {
  const { t } = useTranslation();
  const [sourates, setSourates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);

  // Search states
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("titles"); // "titles" | "fulltext"
  const [results, setResults] = useState(null); // {q,count,results,truncated} | null
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const [scrollVerse, setScrollVerse] = useState(null);

  useEffect(() => {
    axios.get(`${API}/coran/sourates`).then((r) => setSourates(r.data)).catch((e) => console.warn("Coran list load failed:", e));
  }, []);

  useEffect(() => {
    if (!selected) { setCurrent(null); return; }
    setLoading(true);
    axios.get(`${API}/coran/sourate/${selected}`)
      .then((r) => setCurrent(r.data))
      .catch((e) => console.warn("Sourate load failed:", e))
      .finally(() => setLoading(false));
  }, [selected]);

  // Auto-scroll vers le verset cliqué depuis la recherche
  useEffect(() => {
    if (current && scrollVerse) {
      const el = document.querySelector(`[data-testid="verse-${scrollVerse}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[color:var(--papicoo-gold)]", "rounded-lg");
        setTimeout(() => el.classList.remove("ring-2", "ring-[color:var(--papicoo-gold)]", "rounded-lg"), 2500);
      }
      setScrollVerse(null);
    }
  }, [current, scrollVerse]);

  // Full text search (debounced)
  useEffect(() => {
    if (mode !== "fulltext") { setResults(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults(null); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      axios.get(`${API}/coran/search`, { params: { q, limit: 80 } })
        .then((r) => setResults(r.data))
        .catch((e) => { console.warn(e); setResults({ q, count: 0, results: [] }); })
        .finally(() => setSearching(false));
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, mode]);

  const filteredTitles = useMemo(() => {
    if (!query) return sourates;
    const n = query.toLowerCase();
    return sourates.filter((s) =>
      s.name_ar_translit.toLowerCase().includes(n) ||
      s.name_fr.toLowerCase().includes(n) ||
      String(s.num).includes(n)
    );
  }, [query, sourates]);

  const totalVerses = useMemo(() => sourates.reduce((s, x) => s + x.verses_count, 0), [sourates]);

  return (
    <div data-testid="page-coran" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{t("coran.eyebrow")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {t("coran.title")}<br />
        <span className="italic font-light">{t("coran.subtitle")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-3xl">
        114 sourates, {totalVerses.toLocaleString("fr-FR")} versets. Bibliothèque Cheikh Mbacké Sakho — texte purgé de toutes les notes,
        restructuré pour la jeunesse musulmane et offerte gratuitement par Papicoo.
      </p>

      {!selected ? (
        <>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="inline-flex bg-white border border-[color:var(--papicoo-border)] rounded-full p-1 self-start">
              <button
                data-testid="mode-titles"
                onClick={() => { setMode("titles"); setResults(null); }}
                className={`px-4 py-1.5 text-xs rounded-full transition ${mode === "titles" ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)]" : "text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]"}`}
              >{T("Sourates")}</button>
              <button
                data-testid="mode-fulltext"
                onClick={() => setMode("fulltext")}
                className={`px-4 py-1.5 text-xs rounded-full transition ${mode === "fulltext" ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)]" : "text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]"}`}
              >{T("Recherche plein texte")}</button>
            </div>
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--papicoo-muted)]" />
              <input
                data-testid="coran-search"
                placeholder={mode === "fulltext" ? t("coran.search") : t("coran.search")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-white border border-[color:var(--papicoo-border)] rounded-full pl-9 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
              />
              {query && (
                <button
                  data-testid="clear-search"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]"
                  aria-label={T("Effacer")}
                ><X size={14} /></button>
              )}
            </div>
            {searching && <Loader2 size={16} className="animate-spin text-[color:var(--papicoo-muted)]" />}
          </div>

          {mode === "fulltext" ? (
            <div data-testid="fulltext-results" className="mt-6">
              {!results && query.length < 2 && (
                <div className="text-sm italic text-[color:var(--papicoo-muted)] py-10 text-center">
                  {T("Tape au moins 2 lettres pour chercher dans tous les versets.")}
                </div>
              )}
              {results && (
                <>
                  <div className="text-xs text-[color:var(--papicoo-muted)] mb-3">
                    {results.count === 0 ? T("Aucun verset trouvé.") : `${results.count}${results.truncated ? "+" : ""} ${T(results.count > 1 ? "versets pour" : "verset pour")} « ${results.q} »`}
                  </div>
                  <div className="space-y-2">
                    {results.results.map((r) => (
                      <button
                        key={`${r.sourate_num}-${r.verset_num}`}
                        data-testid={`result-${r.sourate_num}-${r.verset_num}`}
                        onClick={() => { setScrollVerse(r.verset_num); setSelected(r.sourate_num); }}
                        className="w-full text-left papicoo-card p-4 hover:bg-[color:var(--papicoo-sand-deep)]/40 transition"
                      >
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)]">
                            Sourate {r.sourate_num} · {r.sourate_name_ar_translit} · verset {r.verset_num}
                          </div>
                          <div className="text-[10px] italic text-[color:var(--papicoo-muted)] shrink-0">{r.sourate_name_fr}</div>
                        </div>
                        <p className="text-[color:var(--papicoo-ink)] leading-relaxed text-sm">
                          {highlight(r.text, results.q)}
                        </p>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTitles.map((s) => (
                <button
                  key={s.num}
                  data-testid={`sourate-${s.num}`}
                  onClick={() => setSelected(s.num)}
                  className="papicoo-card p-4 text-left hover:bg-[color:var(--papicoo-sand-deep)]/40 transition flex items-center gap-3"
                >
                  <div className="shrink-0 w-10 h-10 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] flex items-center justify-center font-display text-lg">
                    {s.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-[color:var(--papicoo-ink)] truncate">{s.name_ar_translit}</div>
                    <div className="text-xs text-[color:var(--papicoo-muted)] truncate">{s.name_fr} · {s.verses_count} versets</div>
                  </div>
                  <BookOpen size={14} className="text-[color:var(--papicoo-gold)] shrink-0" />
                </button>
              ))}
              {filteredTitles.length === 0 && (
                <div className="col-span-full text-center italic text-sm text-[color:var(--papicoo-muted)] py-8">
                  {T("Aucune sourate trouvée. Essaie la recherche plein texte.")}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="mt-7">
          <button data-testid="back-list" onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-sm papicoo-link-underline mb-4">
            <ChevronLeft size={14} /> {T("Toutes les sourates")}
          </button>
          {loading && <div className="text-center italic text-[color:var(--papicoo-muted)] py-12"><Loader2 className="animate-spin mx-auto" /></div>}
          {current && (
            <div data-testid="sourate-detail" className="papicoo-card p-6 sm:p-8">
              <div className="text-center mb-6 pb-4 border-b border-[color:var(--papicoo-border)]">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">Sourate {current.num}</div>
                <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{current.name_ar_translit}</div>
                <div className="italic text-[color:var(--papicoo-muted)] mt-1">{current.name_fr}</div>
              </div>
              <div className="space-y-4">
                {current.verses.map((v) => (
                  <div key={v.num} data-testid={`verse-${v.num}`} className="flex gap-3 p-2 -mx-2 transition">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[color:var(--papicoo-gold)]/20 text-[color:var(--papicoo-ink)] text-xs flex items-center justify-center font-medium">
                      {v.num}
                    </span>
                    <div className="flex-1 min-w-0">
                      {v.text_ar && <p lang="ar" dir="rtl" data-testid={`verse-ar-${v.num}`} className="papicoo-quran text-[color:var(--papicoo-ink)]">{v.text_ar}</p>}
                      <p className="text-[color:var(--papicoo-muted)] leading-relaxed mt-2">{v.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-xs italic text-[color:var(--papicoo-muted)] text-center">
                {T("Traduction française de Muhammad Hamidullah · numérotation Hafs officielle (6 236 versets). Si tu repères une coquille, dis-le à Papicoo.")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
